import type { TemplateField, TemplateFieldOption } from '@branditdev/mf-types'
import { type ChangeEvent, type MouseEvent, type ReactNode, useCallback, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { Input, FormGroup } from 'reactstrap'
import dollar_price from '../general/DollarPrice'
import {
  getOptionValueMap,
  getScalarFieldValue,
  getTemplateFieldValue,
  getTemplateFieldInputName,
  hasTemplateFieldValue,
  isTemplateFieldValueValid,
  type TemplateFieldValuesState
} from './templateFields/templateFieldValues'

type BundleProduct = {
  bundle_group_product_id: string | number
}

type ProductTemplateFieldProps = {
  field: TemplateField
  templateFieldsSelection: TemplateFieldValuesState
  updateProductTemplateField: (data: TemplateField) => void
  updateBundleTemplateSelection: (data: {
    template_field: TemplateField
    bundle_group_product_id: string | number
  }) => void
  dependencyRequirementsMet: (field: TemplateField) => boolean
  isBundle?: boolean
  bundleProduct?: BundleProduct
  currentLocation?: {
    show_price?: boolean
  }
}

type TemplateInputEvent =
  | ChangeEvent<HTMLInputElement | HTMLSelectElement>
  | MouseEvent<HTMLOptionElement | HTMLButtonElement>
type TemplateInputElement = HTMLInputElement | HTMLSelectElement | HTMLOptionElement | HTMLButtonElement

type InputControlType = 'checkbox' | 'datetime-local' | 'radio' | 'select' | 'textarea' | 'text'

type ProductTemplateFieldAction = {
  type: 'UPDATE_PRODUCT_TEMPLATE_FIELD' | 'UPDATE_BUNDLE_TEMPLATE_FIELD'
  payload: unknown
}

type ProductTemplateFieldState = {
  productPage: {
    templateFields: TemplateFieldValuesState
  }
  storeViews: {
    current?: ProductTemplateFieldProps['currentLocation']
  }
}

export const ProductTemplateField = ({
  field,
  templateFieldsSelection,
  updateProductTemplateField,
  updateBundleTemplateSelection,
  dependencyRequirementsMet,
  isBundle,
  bundleProduct,
  currentLocation
}: ProductTemplateFieldProps) => {
  const acceptedAutoSelectInputTypes = ['dropdown', 'radio', 'image_selection']
  const autoSelectedValue =
    field.is_required && acceptedAutoSelectInputTypes.includes(field.input_type) && field.nested_data.length === 1
      ? field.nested_data[0].template_field_option_id
      : ''
  const hasAutoSelectedValue = autoSelectedValue !== ''
  const [error, setError] = useState(false)

  const templateFieldScope = { isBundle, bundleProduct }
  const templateFieldValue = getTemplateFieldValue(templateFieldsSelection, field, templateFieldScope)
  const scalarValue = getScalarFieldValue(templateFieldValue)
  const optionValues = getOptionValueMap(templateFieldValue)
  const hasFieldValue = hasTemplateFieldValue(templateFieldValue)
  const inputName = getTemplateFieldInputName(field, templateFieldScope)
  const hasSortOrder = (option: TemplateFieldOption) => option.sort_order !== null && option.sort_order !== undefined
  const fieldOptions = [...field.nested_data].sort(
    (a, b) => Number(hasSortOrder(b)) - Number(hasSortOrder(a)) || Number(a.sort_order ?? 0) - Number(b.sort_order ?? 0)
  )

  const commitTemplateFieldValue = useCallback(
    (nextValue: TemplateField['value']) => {
      const fieldWithValue = { ...field, value: nextValue }

      if (isBundle && bundleProduct) {
        updateBundleTemplateSelection({
          template_field: fieldWithValue,
          bundle_group_product_id: bundleProduct.bundle_group_product_id
        })
      } else {
        updateProductTemplateField(fieldWithValue)
      }
    },
    [bundleProduct, field, isBundle, updateBundleTemplateSelection, updateProductTemplateField]
  )

  useEffect(() => {
    if (!hasAutoSelectedValue || hasFieldValue) {
      return
    }

    if (field.input_type === 'radio') {
      const optionKey = String(autoSelectedValue)
      commitTemplateFieldValue({ [optionKey]: { [optionKey]: true } })
    } else {
      commitTemplateFieldValue(autoSelectedValue)
    }
  }, [autoSelectedValue, commitTemplateFieldValue, field.input_type, hasAutoSelectedValue, hasFieldValue])

  const dependenciesMet = dependencyRequirementsMet(field)

  const validate = () => {
    setError(!isTemplateFieldValueValid({ ...field, value: templateFieldValue }))
  }

  const selectedOptionData = (optionKey: string) => {
    const selectedOption = optionValues[optionKey]

    return selectedOption && typeof selectedOption === 'object' ? selectedOption : {}
  }

  const handleMultiSelectInput = (option_id: string | number) => {
    const optionKey = String(option_id)
    const optionData = selectedOptionData(optionKey)

    // for radio buttons, only one value can be true
    if (field.input_type === 'radio') {
      const newOptionSelection: Record<string, boolean | string | number> = { [optionKey]: true }

      const selectedQtyBoxQuantity = parseInt(String(optionData.qtyBoxNum), 10)
      if (Number.isInteger(selectedQtyBoxQuantity) && selectedQtyBoxQuantity > 0) {
        newOptionSelection.qtyBoxNum = selectedQtyBoxQuantity
      }

      commitTemplateFieldValue({ [optionKey]: newOptionSelection })
    } else {
      const ifChecked = !!optionData[optionKey]
      commitTemplateFieldValue({
        ...optionValues,
        [optionKey]: { ...optionData, [optionKey]: !ifChecked }
      })
    }
  }

  const handleInput = (e: TemplateInputEvent) => {
    const input_type = field.input_type
    const inputElement = e.currentTarget as TemplateInputElement
    const nextValue = inputElement.value

    if (input_type === 'input' || input_type === 'textarea' || input_type === 'date' || input_type === 'dropdown') {
      commitTemplateFieldValue(nextValue)
    } else if (input_type === 'image_selection') {
      commitTemplateFieldValue(nextValue)
    } else if (input_type === 'multiselect' || input_type === 'checkbox' || input_type === 'radio') {
      handleMultiSelectInput(nextValue)
    }
  }

  // converts to reactstrap Input type to pass to Input component
  const inputType = (): InputControlType => {
    switch (field.input_type) {
      case 'input':
        return 'text'
      case 'dropdown':
      case 'multiselect':
        return 'select'
      case 'date':
        return 'datetime-local'
      default:
        return field.input_type as InputControlType
    }
  }

  const optionLabel = (option: TemplateFieldOption) => {
    const optionPrice = parseInt(String(option.price_cents), 10)
    const optionShippingFee = parseInt(String(option.shipping_fee_cents), 10)
    const showPrice = currentLocation?.show_price !== false

    let result: ReactNode = (
      <>
        {option.option_title}
        {showPrice && optionPrice > 0 ? (
          <>
            {' '}
            - {dollar_price(optionPrice)} {option.quantity ? `[QTY: ${option.quantity}]` : ''}
          </>
        ) : (
          ''
        )}
      </>
    )

    // if option has additional shipping price, display it
    if (showPrice && optionShippingFee > 0) {
      result = (
        <>
          {result} - {dollar_price(optionShippingFee)} Shipping Fee
        </>
      )
    }
    return result
  }

  const handleQtyChange = (e: ChangeEvent<HTMLInputElement>, option: TemplateFieldOption) => {
    const optionKey = String(option.template_field_option_id)
    const qtyBoxNum = e.target.value

    commitTemplateFieldValue({
      ...optionValues,
      [optionKey]: { ...selectedOptionData(optionKey), qtyBoxNum }
    })
  }

  const InputField = () => {
    // for fields with options (dropdown)
    const renderOptions = () => {
      const optionsTags = fieldOptions.map((option, i) => {
        return (
          <option key={i} value={option.template_field_option_id}>
            {optionLabel(option)}
          </option>
        )
      })

      optionsTags.unshift(
        <option key={-1} value="-1">
          Please Select
        </option>
      )
      return optionsTags
    }

    return (
      <Input
        onBlur={validate}
        className="mf-mt-10"
        type={inputType()}
        name="field_input"
        value={scalarValue}
        onChange={handleInput}
      >
        {inputType() === 'select' ? renderOptions() : null}
      </Input>
    )
  }

  const FieldLabel = () => {
    return (
      <>
        <h5>{field.field_title}</h5>
        {field.description ? <label>{field.description}</label> : null}
      </>
    )
  }

  const ShowQtyBox = (option: TemplateFieldOption) => {
    return (
      <>
        {option.show_qty_box ? (
          <>
            <Input
              className="form-control"
              type="number"
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleQtyChange(e, option)}
              defaultValue={option.quantity ? option.quantity : 1}
              min={1}
              style={{
                maxWidth: '35%',
                marginLeft: '10px',
                height: '30px',
                fontSize: '0.9rem'
              }}
            />
            <label className="mf-mb-0">&nbsp;Qty</label>
          </>
        ) : null}
      </>
    )
  }

  const formatExtraPrice = (cents: number) => {
    const formattedValue = dollar_price(cents)
    if (cents > 0) {
      return <> + {formattedValue}</>
    } else if (cents < 0) {
      return <> - {formattedValue}</>
    } else {
      return ''
    }
  }

  const imageSelectionHeader = (fieldOptions: TemplateFieldOption[]) => {
    const selectedOption = scalarValue
      ? fieldOptions.find(opt => String(opt.template_field_option_id) === String(scalarValue))
      : null
    const extraPrice = selectedOption ? formatExtraPrice(Number(selectedOption.price_cents)) : ''

    return (
      <>
        {!scalarValue || !selectedOption ? (
          <b>Please Select An Image</b>
        ) : (
          <>
            <b>Image: </b>
            {selectedOption.option_title}
            {currentLocation?.show_price ? extraPrice : null}
          </>
        )}
      </>
    )
  }

  const SwitchInputField = (inputType: string) => {
    switch (inputType) {
      case 'multiselect':
        return (
          <select multiple style={{ width: '100%' }}>
            {fieldOptions.map((option, i) => {
              const currentValue = optionValues[String(option.template_field_option_id)]
              const conditionalStyle = {
                backgroundColor: '#B4D5FE'
              }

              return (
                <option
                  className="noselect"
                  key={i}
                  value={option.template_field_option_id}
                  style={currentValue ? conditionalStyle : {}}
                  selected={!!currentValue}
                  onClick={handleInput}
                >
                  {optionLabel(option)}
                </option>
              )
            })}
          </select>
        )
      case 'image_selection':
        return (
          <>
            {imageSelectionHeader(fieldOptions)}
            <ul className="mf-mt-2" style={{ display: 'flex', flexWrap: 'wrap' }}>
              {fieldOptions.map((option, i) => {
                return (
                  <div
                    key={i}
                    className="mf-mr-2 mf-mb-2"
                    style={{ width: '125px', height: '125px', backgroundColor: 'white', borderRadius: '10%' }}
                  >
                    <button
                      value={option.template_field_option_id}
                      key={i}
                      onClick={handleInput}
                      className={
                        String(option.template_field_option_id) === String(scalarValue)
                          ? 'template-image template-image-selected'
                          : 'template-image template-non-selected-image'
                      }
                      style={{ width: '125px', height: '125px' }}
                    >
                      <img
                        src={option.image?.initial?.url}
                        style={{ borderRadius: '10%' }}
                        alt={String(option.option_title ?? '')}
                      />
                    </button>
                  </div>
                )
              })}
            </ul>
          </>
        )
      case 'radio':
      case 'checkbox':
        return fieldOptions.map((option, i) => {
          const optionKey = String(option.template_field_option_id)
          const isChecked = !!selectedOptionData(optionKey)[optionKey]

          return (
            <FormGroup
              check
              key={i}
              style={{ margin: '0', marginBottom: '0.5rem', display: 'flex', alignItems: 'center' }}
            >
              <Input
                type={field.input_type === 'radio' ? 'radio' : 'checkbox'}
                name={inputName}
                onChange={handleInput}
                value={option.template_field_option_id}
                style={{ display: 'flex' }}
                checked={isChecked}
              />
              <div className="form-row center">
                <label className="mf-mb-0">&nbsp;{optionLabel(option)}</label>
                {ShowQtyBox(option)}
              </div>
            </FormGroup>
          )
        })
      default:
        return InputField()
    }
  }

  return (
    <FormGroup style={{ margin: '0' }}>
      {dependenciesMet === true ? (
        <>
          {FieldLabel()}
          {SwitchInputField(field.input_type)}
        </>
      ) : null}
      {error ? <p className="text-danger mf-mt-2 mf-mb-0">Invalid input</p> : null}
    </FormGroup>
  )
}

const mapDispatchToProps = (dispatch: (action: ProductTemplateFieldAction) => void) => {
  return {
    updateProductTemplateField: (data: TemplateField) =>
      dispatch({ type: 'UPDATE_PRODUCT_TEMPLATE_FIELD', payload: data }),
    updateBundleTemplateSelection: (data: {
      template_field: TemplateField
      bundle_group_product_id: string | number
    }) => dispatch({ type: 'UPDATE_BUNDLE_TEMPLATE_FIELD', payload: data })
  }
}

const mapStateToProps = (state: ProductTemplateFieldState) => {
  return {
    currentLocation: state.storeViews.current,
    templateFieldsSelection: state.productPage.templateFields
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ProductTemplateField)
